import { useState, useEffect, useMemo, useCallback } from "react";
import {
  BodyComponent,
  TableComponent,
  ActionComponent,
  trans,
  HeaderComponent,
  EmployeeWithProfile,
  PaginationComponent, CustomButton
} from "@comps/components";
import { makeRequest } from "@utils/helpers";
import { Commons, Const, colors } from "../../constants";
import { Badge, message, Popconfirm, Tooltip } from 'antd';
import { FilterComponent, FilterButton, ButtonComponent } from "@comps/components";
import { getAllDataReq, getFilterReq } from "./requests";
import moment from "moment/moment";
import RequestAsTab from "../../../common/RequestAsTab";
import ViewFlow from '@mods/common/ViewFlow';
import UpdateLeaveApproval from "../../common/updateLeaveApproval";
import ViewLeaveApprovalHistory from '../../common/ViewLeaveApprovalHistory';
import { getRequestFilters } from '@mods/common/commonFilterFields';
import { approveBulkRequests } from "@mods/common/requests";
import { useLocation } from "react-router-dom";
import { deleteDataReq, updateDataReq } from "../companyLeaves/requests";
import { WarningOutlined } from "@ant-design/icons";
import { RefreshOutlined } from "@mui/icons-material";
import { checkPermissions } from "../../../../wrappers/commonFunctions";

const langKey = Const["leaveApproval"].lngKey;
const pageConfig = {
  headers: {
    title: trans(`${langKey}.moduleName`),
    breadcrumb: [
      {
        name: trans(`${langKey}.moduleName`),
        path: Const["leaveApproval"].route
      }
    ]
  }
}

const IndexLeaveApprovals = (props) => {
  const [dataSource, setDataSource] = useState([]);
  const [loader, setLoader] = useState(false);
  const [childComponent, setChildComponent] = useState(null);

  const [asFilters, setAsFilters] = useState([]);
  const [requestAs, setRequestAs] = useState(8)
  const [filters, setFilters] = useState();
  const [delegator, setDelegator] = useState('')
  //start pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    "total": 0,
    "count": 0,
    "per_page": 0,
    "current_page": 0,
    "total_pages": 0,
    "links": {}
  });
  //end pagination states

  // ==================================
  //   Use States End
  // ==================================

  // ==================================
  //   Table Data End
  // ==================================
  const location = useLocation();
  useEffect(() => {
    getAllData();
    // eslint-disable-next-line
  }, [location.pathname, filters, requestAs, currentPage]);

  const getAllData = () => {

    let payload = {
      page: currentPage,
      filters: filters,
      data: { request_as: requestAs, delegator_id: delegator },
      api: props.api
    }

    if (props.api === 'leaveDelegateApprovals' && !delegator) {
      setDataSource([])
      setAsFilters([]);
      onOpenFilter()
      return;
    }
    makeRequest(setLoader, getAllDataReq, payload, onSuccess, onErrorGetting);
  };
  const onErrorGetting = (err, res) => {
    message.error(res.response.data.message);
    setDataSource([]);
    setAsFilters([]);
  }
  const onSuccess = (response, data) => {
    message.info(data.message);
    setPagination(response.leave_requests);
    setDataSource(response.leave_requests.data);
    setAsFilters(response.filters);
  };

  const onEdit = useCallback((record) => {
    setChildComponent(
      <UpdateLeaveApproval onCompleted={ onCompleted } record={ record } disabled={ false } requestAsId={ requestAs } delegatorId={ delegator } api={ props.api } filters={ filters } />
    );
  }, [delegator, filters, props.api, requestAs]);

  const onView = useCallback((record) => {
    setChildComponent(
      <UpdateLeaveApproval onCompleted={ onCompleted } record={ record } disabled={ true } requestAsId={ requestAs } />
    );
  }, [requestAs]);
  const onViewHistory = useCallback((record) => {
    setChildComponent(
      <ViewLeaveApprovalHistory onCompleted={ onCompleted } record={ record } disabled={ true } />
    );
  }, []);
  const onViewFlow = useCallback((record) => {
    setChildComponent(
      <ViewFlow onCompleted={ onCompleted } record={ record } disabled={ true } />
    );
  }, []);

  const onError = (err, res) => {
    message.error(res.response.data.message);
  }
  const onDelete = useCallback((record) => {
    const payload = {
      filters: '', id: record.id
    }
    makeRequest(setLoader, deleteDataReq, payload, onDeleted, onError);
  }, []);
  const onDeleted = useCallback((res) => {
    getAllData();
    message.success("Record Deleted")
  }, [currentPage, delegator, filters, props.api, requestAs]);
  const onReopen = useCallback((record) => {
    let data = record;
    data.action = 'edit'
    data.status = 'Pending'
    data.is_rejected = false
    data.is_completed = false
    data.is_canceled = false
    data.is_pending = true
    data.shiftIds = []
    const payload = { filters: filters, file_attachment: undefined, leave_request: { ...data, shiftIds: [] } };
    // makeRequest(setLoader, updateDataReq, payload, onCompleted, onError);
    onDelete()
  }, [filters, onDelete]);

  const onCompleted = useCallback((data) => {
    if (data.length) {
      setDataSource(data);
    }
    setChildComponent(null);
  }, []);

  const columns = useMemo(() => [
    // {
    //   key: 'code',
    //   title: trans(Commons.code),
    //   dataIndex: "code",
    // },
    {
      key: 'employment_id',
      title: trans(`${langKey}.employment_id`),
      render: (record) => {
        return <EmployeeWithProfile record={ record.requestor } />
      },
    },
    {
      key: 'department_name',
      title: trans(`${langKey}.department_name`),
      render: (record) => {
        return record.requestor.department_name;
      }
    },
    {
      key: 'leave_type_name',
      title: trans(`${langKey}.leave_type_name`),
      dataIndex: "leave_type_name",
    },
    {
      key: 'next_requestee',
      title: trans(`${langKey}.next_requestee`),
      render: (record) => {
        return <EmployeeWithProfile record={ record.next_requestee_name } />
      },
    },
    {
      key: 'count',
      title: trans(`${langKey}.count`),
      dataIndex: 'count'
    },
    {
      key: 'dates',
      title: trans(`${langKey}.dates`),
      render: (record) => {
        return moment(record.start).format('YYYY-MM-DD') + '-to-' + moment(record.end).format('YYYY-MM-DD')
      }
    },
    {
      key: 'status',
      title: trans(`${langKey}.status`),
      render: (record) => {
        return <Badge
          count={ record.status ? record.status : 'none' }
          style={ { background: colors[record.status_class], color: colors[record.status_class + '-color'] } }
        />
      }
    },
    {
      key: trans(Commons.actions),
      title: trans(Commons.actions), fixed: 'right',
      render: (record) => (
        <ActionComponent
          each={ record } modkey={ props.modkey }
          onViewHistory={ (record.status === 'Completed' || record.status === 'Rejected' || record.status === 'Cancelled') ? onViewHistory : '' }
          onViewFlow={ record.flow.length !== 0 ? onViewFlow : '' }
          onEdit={ (record.can_approve || record.can_reject || record.can_consent || record.status === 'Pending') ? onEdit : '' }
          onView={ onView }
          onDelete={ onDelete }
        >
          <Popconfirm title="Are you sure to Reopen this request?" icon={ <WarningOutlined /> } onConfirm={ () => onReopen(record) }>
            { record.status === 'Rejected' && <Tooltip title="Re-open this request"><ButtonComponent className="text-warning" icon={ <RefreshOutlined /> } disabled={ !checkPermissions(props.modkey, 'update') }></ButtonComponent></Tooltip> }
          </Popconfirm>
        </ActionComponent>
      ),
    },
  ], [onDelete, onEdit, onReopen, onView, onViewFlow, onViewHistory, props.modkey]);

  const handleTableChange = (page, fil, sorter) => {
    let payload = {
      ...pagination,
      current: page.current,
      pageSize: page.pageSize,
      sortName: sorter.field || "id",
      sortType: sorter.order === "ascend" ? "asc" : "desc",
    };
    setPagination(payload);
  };

  //---------------filters-----------------------
  const onOpenFilter = () => {
    setChildComponent(<FilterComponent filters={ filterFields } modkey={ props.modkey } selected={ filters } api={ getFilterReq } setCurrentPage={ setCurrentPage } onFilter={ onApplyFilter } onCloseFilter={ onCloseFilter }
    />
    )
  }
  const onApplyFilter = (filters) => {
    setFilters(filters);
    getDelegatorId(filters)
    setChildComponent(null);
  }
  const getDelegatorId = (filters) => {
    // Given string
    const inputString = filters;
    // Regular expression pattern to match the desired string
    const pattern = /delegator_\|(\w{8}-\w{4}-\w{4}-\w{4}-\w{12})/;
    //Extracting the match from the string
    const match = inputString && inputString.match(pattern);
    // Check if a match is found
    if (match) {
      const delegatorValue = match[1]; // Extract the UUID part
      setDelegator(delegatorValue)
    } else {
      setDelegator(delegator)
    }
  }
  const onCloseFilter = () => {
    setChildComponent(null);
  }

  //----end filter--------------------
  //================Start bulk approved=================================
  const [selectedRows, setSelectedRows] = useState([]);
  const rowSelection = useMemo(() => ({
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRows(selectedRows)
    },
    getCheckboxProps: (record) => ({
      disabled: record.status !== 'Pending',
      name: record.name,
    }),
  }), []);
  const tableRowSelection = useMemo(() => ({
    type: 'checkbox',
    ...rowSelection,
  }), [rowSelection]);
  const onApproveBulk = (isApproval) => {
    const data = [];
    selectedRows.forEach((request, key) => {
      request.request_as = requestAs
      request.is_approval = isApproval
      //request.last_remarks = 'bulk'
      //request.remarks = 'bulk'
      const payload = {
        request: request, delegator_id: delegator
      }
      data.push(payload)
    })
    const bulkData = {
      data: data,
      api: props.api + "/approve-bulk",
      method: 'put'
    }
    makeRequest(setLoader, approveBulkRequests, bulkData, onBulkApproved, onError);
  }
  const onBulkApproved = (approval) => {
    setSelectedRows([])
    message.success('Success')
    getAllData();
  }
  //================End bulk approved=================================


  //==============================================================================
  return (
    <>

      { childComponent }
      <BodyComponent>
        <RequestAsTab getAllData={ getAllData } setAsFilters={ asFilters } setRequestAs={ setRequestAs } />
        <HeaderComponent headers={ pageConfig.headers } modkey={ props.modkey }>
          <Popconfirm
            description="Which Action You Want to Perform?"
            okText="Approve"
            cancelText="Reject"
            onConfirm={ () => onApproveBulk(true) }
            onCancel={ () => onApproveBulk(false) }
          >
            { selectedRows.length > 0 && <CustomButton loading={ loader } key="create_button" text='Approve / Reject' /> }
          </Popconfirm>
          <FilterButton onClick={ onOpenFilter } />
        </HeaderComponent>
        <TableComponent modkey={ props.modkey }
          loader={ loader }
          columns={ columns }
          dataSource={ dataSource }
          onChange={ handleTableChange }
          rowSelection={ tableRowSelection }
        />

      </BodyComponent>
      <PaginationComponent pagination={ pagination } modkey={ props.modkey } setCurrentPage={ setCurrentPage } currentPage={ currentPage } />
    </>
  );
};

export default IndexLeaveApprovals;
const filterFields = getRequestFilters('')
